import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BRANCH_CODES,
  SUPPLY_TYPES,
  type BranchCode,
  type ImportTargetsRow,
  type SupplyType,
} from '@lorsamthai/ops-shared';
import {
  downloadTargetsTemplate,
  importTargets,
  type ImportTargetsResult,
} from '../lib/queries.js';

type Mode = 'all' | 'configured' | 'unconfigured';

interface ParsedRow {
  raw: Record<string, string>;
  rowNum: number;
  parsed?: ImportTargetsRow;
  parseError?: string;
}

/**
 * Tiny CSV parser: handles double-quoted fields with escaped quotes.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = '';
  let inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuote) {
      if (ch === '"' && text[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuote = false;
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"' && cur === '') inQuote = true;
      else if (ch === ',') {
        row.push(cur);
        cur = '';
      } else if (ch === '\r') {
        // skip
      } else if (ch === '\n') {
        row.push(cur);
        cur = '';
        rows.push(row);
        row = [];
      } else cur += ch;
    }
  }
  if (cur !== '' || row.length > 0) {
    row.push(cur);
    rows.push(row);
  }
  return rows;
}

const REQUIRED_HEADERS = ['sku', 'branch'];
const OPTIONAL_NUM_HEADERS = ['min_qty', 'target_qty', 'safety_days', 'primary_supplier_id'];

function parseRow(raw: Record<string, string>, rowNum: number): ParsedRow {
  const sku = raw.sku?.trim() ?? '';
  const branch = raw.branch?.trim() ?? '';

  if (!sku || !branch) {
    return { raw, rowNum, parseError: 'sku และ branch ห้ามว่าง' };
  }
  if (!BRANCH_CODES.includes(branch as BranchCode)) {
    return { raw, rowNum, parseError: `branch '${branch}' ไม่ถูกต้อง (ww/bp/twn/llk)` };
  }

  const out: ImportTargetsRow = {
    sku,
    branch: branch as BranchCode,
  };

  // SKU-level metadata (brand/model/size) — passed through for SKUs not in
  // tire_catalog. Server upserts these into ops.sku_meta. Tire-catalog SKUs
  // ignore them (catalog wins).
  const brand = raw.brand?.trim();
  const model = raw.model?.trim();
  const size = raw.size?.trim();
  if (brand) out.brand = brand;
  if (model) out.model = model;
  if (size) out.size = size;

  for (const k of OPTIONAL_NUM_HEADERS) {
    const v = raw[k]?.trim();
    if (v === undefined || v === '') continue;
    const n = Number(v);
    if (!Number.isFinite(n)) {
      return { raw, rowNum, parseError: `${k} '${v}' ไม่ใช่ตัวเลข` };
    }
    // Excel often turns blank cells into 0. Treat 0 as "leave blank" for
    // fields that the server requires to be > 0; otherwise the row 400s.
    if (k === 'primary_supplier_id') {
      if (n <= 0) continue; // 0 = no supplier, leave the field unset
      out.primary_supplier_id = Math.round(n);
    } else if (k === 'target_qty') {
      if (n <= 0) continue; // target_qty must be > 0; treat 0 as "don't change"
      out.target_qty = Math.round(n);
    } else {
      // min_qty / safety_days accept 0 — store as int
      (out as unknown as Record<string, number>)[k] = Math.round(n);
    }
  }

  const supplyTypeRaw = raw.supply_type?.trim();
  if (supplyTypeRaw) {
    if (!SUPPLY_TYPES.includes(supplyTypeRaw as SupplyType)) {
      return {
        raw,
        rowNum,
        parseError: `supply_type '${supplyTypeRaw}' ไม่ถูก (stock/production)`,
      };
    }
    out.supply_type = supplyTypeRaw as SupplyType;
  }

  const autoCalcRaw = raw.auto_calc?.trim().toLowerCase();
  if (autoCalcRaw) {
    if (autoCalcRaw === 'true' || autoCalcRaw === '1' || autoCalcRaw === 'yes') {
      out.auto_calc = true;
    } else if (autoCalcRaw === 'false' || autoCalcRaw === '0' || autoCalcRaw === 'no') {
      out.auto_calc = false;
    } else {
      return {
        raw,
        rowNum,
        parseError: `auto_calc '${autoCalcRaw}' ต้องเป็น true/false`,
      };
    }
  }

  return { raw, rowNum, parsed: out };
}

export default function TargetsImport() {
  const [mode, setMode] = useState<Mode>('all');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [parseSummary, setParseSummary] = useState<string | null>(null);

  const [plan, setPlan] = useState<ImportTargetsResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [committed, setCommitted] = useState<ImportTargetsResult | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);

  const download = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadTargetsTemplate(mode);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (f: File | null) => {
    setFile(f);
    setParsedRows([]);
    setPlan(null);
    setCommitted(null);
    setPlanError(null);
    setParseSummary(null);
    if (!f) return;

    const text = await f.text();
    const grid = parseCsv(text);
    if (grid.length === 0) {
      setParseSummary('ไฟล์ว่าง');
      return;
    }
    // First row = headers (strip BOM if present)
    const headers = grid[0]!.map((h, i) =>
      (i === 0 ? h.replace(/^﻿/, '') : h).trim()
    );
    const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
    if (missing.length > 0) {
      setParseSummary(`ขาดคอลัมน์ที่จำเป็น: ${missing.join(', ')}`);
      return;
    }

    const rows: ParsedRow[] = [];
    for (let i = 1; i < grid.length; i++) {
      const cells = grid[i]!;
      // Skip blank rows (all empty)
      if (cells.every((c) => c.trim() === '')) continue;
      const obj: Record<string, string> = {};
      headers.forEach((h, j) => {
        obj[h] = cells[j] ?? '';
      });
      rows.push(parseRow(obj, i + 1)); // +1 because row 1 is header
    }
    setParsedRows(rows);
    const okCount = rows.filter((r) => r.parsed).length;
    const errCount = rows.length - okCount;
    setParseSummary(`อ่านได้ ${rows.length} แถว · ผ่าน ${okCount} · ผิดรูปแบบ ${errCount}`);
  };

  /** Format an api() error including server-side validation details if any. */
  const fmtError = (err: unknown, fallback: string): string => {
    if (!(err instanceof Error)) return fallback;
    const e = err as Error & { status?: number; code?: string; details?: unknown };
    const parts: string[] = [];
    if (e.status) parts.push(`HTTP ${e.status}`);
    if (e.message) parts.push(e.message);
    if (e.details && typeof e.details === 'object') {
      try {
        parts.push(JSON.stringify(e.details).slice(0, 500));
      } catch {
        // ignore
      }
    }
    return parts.join(' · ') || fallback;
  };

  const dryRun = async () => {
    setConfirming(true);
    setPlan(null);
    setPlanError(null);
    try {
      const validRows = parsedRows
        .filter((r) => r.parsed)
        .map((r) => r.parsed!);
      if (validRows.length === 0) {
        setPlanError('ไม่มีแถวที่ผ่าน parse ในไฟล์');
        return;
      }
      const result = await importTargets({ rows: validRows, dry_run: true });
      setPlan(result);
    } catch (err) {
      setPlanError(fmtError(err, 'dry-run failed'));
    } finally {
      setConfirming(false);
    }
  };

  const commit = async () => {
    setConfirming(true);
    setCommitted(null);
    setPlanError(null);
    try {
      const validRows = parsedRows
        .filter((r) => r.parsed)
        .map((r) => r.parsed!);
      const result = await importTargets({ rows: validRows, dry_run: false });
      setCommitted(result);
    } catch (err) {
      setPlanError(fmtError(err, 'commit failed'));
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setFile(null);
    setParsedRows([]);
    setParseSummary(null);
    setPlan(null);
    setCommitted(null);
    setPlanError(null);
  };

  const parseErrors = parsedRows.filter((r) => r.parseError);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/stock" className="text-xs text-slate-500 hover:underline">
          ← กลับ Stock
        </Link>
        <h1 className="mt-1 text-2xl font-bold">Import Stock Targets</h1>
        <p className="mt-1 text-sm text-slate-500">
          ดาวน์โหลด template → แก้ใน Excel/Sheets → อัพโหลดกลับ
        </p>
      </div>

      {/* Step 1: Download */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-base font-semibold">1. ดาวน์โหลด template</h2>
        <p className="mt-1 text-xs text-slate-500">
          brand/model/size = กรอกได้สำหรับ SKU ภายในที่ไม่อยู่ใน tire_catalog
          (ระบบเก็บเข้า ops.sku_meta) · supplier_name = read-only ใช้ดูเฉยๆ
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
          >
            <option value="all">ทุก SKU × ทุกสาขา</option>
            <option value="configured">เฉพาะที่ตั้งค่าแล้ว</option>
            <option value="unconfigured">เฉพาะที่ยังไม่ตั้งค่า</option>
          </select>
          <button
            onClick={download}
            disabled={downloading}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white disabled:opacity-60"
          >
            {downloading ? '⏳ กำลังดาวน์โหลด...' : '📥 ดาวน์โหลด CSV'}
          </button>
        </div>
        {downloadError && (
          <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {downloadError}
          </div>
        )}
      </section>

      {/* Step 2: Upload */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-base font-semibold">2. อัพโหลดไฟล์ที่แก้แล้ว</h2>
        <p className="mt-1 text-xs text-slate-500">
          ใส่ค่าใน min_qty, target_qty, safety_days, supply_type,
          primary_supplier_id, auto_calc — ช่องว่าง = ไม่แก้ค่าเดิม
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
            className="text-sm"
          />
          {file && (
            <button
              onClick={reset}
              className="rounded-md border border-slate-200 px-3 py-1 text-xs hover:bg-slate-50"
            >
              ล้าง
            </button>
          )}
        </div>
        {parseSummary && (
          <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
            {parseSummary}
          </div>
        )}
        {parseErrors.length > 0 && (
          <details className="mt-3 group">
            <summary className="cursor-pointer text-xs text-amber-700 hover:text-amber-900">
              ▸ {parseErrors.length} แถวอ่านไม่ผ่าน — ดูรายละเอียด
            </summary>
            <ul className="mt-2 max-h-48 overflow-y-auto space-y-1 text-xs">
              {parseErrors.slice(0, 50).map((r) => (
                <li key={r.rowNum} className="rounded bg-amber-50 px-2 py-1">
                  <span className="font-mono">แถว {r.rowNum}</span>{' '}
                  <span className="text-amber-900">{r.parseError}</span>
                </li>
              ))}
              {parseErrors.length > 50 && (
                <li className="text-slate-500">และอีก {parseErrors.length - 50} แถว...</li>
              )}
            </ul>
          </details>
        )}
      </section>

      {/* Step 3: Dry-run + confirm */}
      {parsedRows.some((r) => r.parsed) && !committed && (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-base font-semibold">3. ตรวจสอบ + ยืนยัน</h2>
          {!plan && (
            <>
              <p className="mt-1 text-xs text-slate-500">
                กดดูแผนก่อน เพื่อให้ระบบตรวจ supplier_id และ constraint
                อื่นๆ — ยังไม่เขียน DB
              </p>
              <button
                onClick={dryRun}
                disabled={confirming}
                className="mt-3 rounded-md border border-slate-300 bg-white px-4 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                {confirming ? '⏳ กำลังตรวจ...' : '🔍 ตรวจสอบ (dry-run)'}
              </button>
            </>
          )}

          {plan && (
            <>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <Stat label="แถวใหม่" value={plan.inserted} tone="ok" />
                <Stat label="แถวอัพเดต" value={plan.updated} tone="info" />
                <Stat label="ข้าม / ผิดพลาด" value={plan.skipped} tone={plan.skipped > 0 ? 'warn' : 'neutral'} />
              </div>

              {plan.errors.length > 0 && (
                <details className="mt-3 group" open={plan.errors.length <= 10}>
                  <summary className="cursor-pointer text-xs text-amber-700 hover:text-amber-900">
                    ▸ {plan.errors.length} แถวจะถูกข้าม
                  </summary>
                  <ul className="mt-2 max-h-48 overflow-y-auto space-y-1 text-xs">
                    {plan.errors.slice(0, 50).map((e, i) => (
                      <li key={i} className="rounded bg-amber-50 px-2 py-1">
                        <span className="font-mono">
                          แถว {e.row} {e.sku && `· ${e.sku}`}
                          {e.branch && ` · ${e.branch}`}
                        </span>{' '}
                        <span className="text-amber-900">{e.message}</span>
                      </li>
                    ))}
                    {plan.errors.length > 50 && (
                      <li className="text-slate-500">และอีก {plan.errors.length - 50} แถว...</li>
                    )}
                  </ul>
                </details>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={commit}
                  disabled={confirming || plan.inserted + plan.updated === 0}
                  className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {confirming
                    ? 'กำลังบันทึก...'
                    : `✓ ยืนยันบันทึก ${plan.inserted + plan.updated} แถว`}
                </button>
                <button
                  onClick={() => setPlan(null)}
                  disabled={confirming}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                >
                  ตรวจใหม่
                </button>
              </div>
            </>
          )}

          {planError && (
            <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
              {planError}
            </div>
          )}
        </section>
      )}

      {/* Done */}
      {committed && (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <h2 className="text-base font-semibold text-emerald-900">✅ Import เสร็จสิ้น</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <Stat label="เพิ่มใหม่" value={committed.inserted} tone="ok" />
            <Stat label="อัพเดต" value={committed.updated} tone="info" />
            <Stat label="ข้าม" value={committed.skipped} tone="neutral" />
          </div>
          <p className="mt-3 text-xs text-emerald-900">
            ทุกการเปลี่ยนถูก log ใน <code>ops.stock_targets_history</code>{' '}
            (ดูใน Supabase ได้)
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/stock"
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              กลับหน้า Stock
            </Link>
            <button
              onClick={reset}
              className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm hover:bg-slate-100"
            >
              Import อีกไฟล์
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ok' | 'info' | 'warn' | 'neutral';
}) {
  const toneClasses: Record<typeof tone, string> = {
    ok: 'bg-emerald-50 text-emerald-900 ring-emerald-200',
    info: 'bg-sky-50 text-sky-900 ring-sky-200',
    warn: 'bg-amber-50 text-amber-900 ring-amber-200',
    neutral: 'bg-slate-50 text-slate-700 ring-slate-200',
  };
  return (
    <div className={`rounded-md p-3 ring-1 ring-inset ${toneClasses[tone]}`}>
      <div className="text-xs">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}
